import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Modal, Table } from 'antd';

export const BOOKINGS_PAGE_SIZE = 20;

const formatMoney = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const channelLabel = platform => {
  const key = String(platform || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const labels = {
    airbnb: 'Airbnb',
    agoda: 'Agoda',
    agodahomes: 'Agoda Homes',
    bookingcom: 'Booking.com',
    bookingdotcom: 'Booking.com',
    ctrip: 'Ctrip',
    ctripcm: 'Ctrip',
    expedia: 'Expedia',
    traveloka: 'Traveloka',
    tiketcom: 'Tiket.com',
    ticketdotcom: 'Tiket.com',
    hostplatform: 'HostPlatform',
    maplehome: 'Maple Home',
    maple: 'Maple Home',
    offline: 'Walk-in',
    walkin: 'Walk-in',
  };
  return labels[key] || platform || '';
};

const bookingRowId = row => String((row && row.reservationId) || (row && row.code) || '');

export const sortContributionsByReservationId = rows =>
  (rows || []).slice().sort((a, b) => bookingRowId(a).localeCompare(bookingRowId(b)));

export const draftFeeAmount = (draftByResId, row) => {
  const id = bookingRowId(row);
  if (id && Object.prototype.hasOwnProperty.call(draftByResId || {}, id)) {
    const n = Number(draftByResId[id]);
    return Number.isFinite(n) ? n : 0;
  }
  return Number(row && row.cleaningFee) || 0;
};

const formatStay = row => {
  const start = row && row.checkInDate ? String(row.checkInDate).slice(0, 10) : '-';
  const end = row && row.checkOutDate ? String(row.checkOutDate).slice(0, 10) : '-';
  return `${start} - ${end}`;
};

const RevenueBookingsModal = ({
  visible,
  packageName,
  contributions,
  unitMetaById,
  savingReservationId,
  onCancel,
  onSaveFee,
}) => {
  const [draftByResId, setDraftByResId] = useState({});

  useEffect(() => {
    if (!visible) setDraftByResId({});
  }, [visible]);

  const rows = sortContributionsByReservationId((contributions || []).filter(c => c && c.status !== 'cancelled'));
  const titleName = packageName || 'Bookings';

  const setDraft = (id, val) => {
    setDraftByResId(prev => {
      const next = Object.assign({}, prev);
      next[id] = val == null ? 0 : Number(val);
      return next;
    });
  };

  const handleSave = row => {
    if (!onSaveFee) return;
    onSaveFee({
      reservationId: row.reservationId,
      amount: draftFeeAmount(draftByResId, row),
    });
  };

  return (
    <Modal
      visible={!!visible}
      title={`${titleName} — ${rows.length} booking${rows.length === 1 ? '' : 's'}`}
      onCancel={onCancel}
      footer={null}
      width={960}
      destroyOnClose
    >
      <Table
        size="small"
        rowKey={r => bookingRowId(r)}
        dataSource={rows}
        pagination={{
          pageSize: BOOKINGS_PAGE_SIZE,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        columns={[
          {
            title: 'Confirmation',
            key: 'code',
            render: r => r.code || String(r.reservationId || ''),
          },
          {
            title: 'Dates',
            key: 'dates',
            render: r => formatStay(r),
          },
          {
            title: 'Unit',
            key: 'unit',
            render: r => {
              const unitId = r.unitId ? String(r.unitId) : '';
              const meta = (unitMetaById && unitMetaById[unitId]) || {};
              return meta.unitName || unitId || '—';
            },
          },
          {
            title: 'Channel',
            key: 'channel',
            render: r => channelLabel(r.platform),
          },
          {
            title: 'Gross',
            key: 'gross',
            align: 'right',
            render: r => formatMoney(r.grossAmount != null ? r.grossAmount : r.amount),
          },
          {
            title: 'Cleaning fee',
            key: 'cleaningFee',
            width: 220,
            render: r => {
              const id = bookingRowId(r);
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <InputNumber
                    min={0}
                    precision={2}
                    value={draftFeeAmount(draftByResId, r)}
                    onChange={val => setDraft(id, val)}
                    style={{ width: 110 }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    loading={String(savingReservationId || '') === id}
                    onClick={() => handleSave(r)}
                  >
                    Save
                  </Button>
                </span>
              );
            },
          },
          {
            title: 'Net (room rate)',
            key: 'net',
            align: 'right',
            render: r => formatMoney(r.amount),
          },
        ]}
      />
    </Modal>
  );
};

export default RevenueBookingsModal;
